import { ReactElement, useContext, useEffect, useRef, useState } from "react";
import { WindowGridContainer } from "../components/WindowGridContainer";
import React from "react";
import { Grid, Tooltip, Typography, styled, Box } from "@mui/material";
import { GameState } from "./Room";
import { HttpClient } from "../api/HttpClient";
import { useAlert } from "../contexts/alert/useAlert";
import { CardDeckType, GameInsightsResponse } from "../api/Requests";
import moment, { Moment } from "moment";
import { UserContext } from "../contexts/UserContext";
import AlignmentBadgeComponent from "../components/AlignmentBadge";

const ItemTitleGrid = styled(Grid)({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
});

const ItemValueGrid = styled(Grid)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});

const ItemTitleWaitingGrid = styled(Grid)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});

const TitleTypography = styled(Typography)({
    paddingLeft: 8, // 1 * 8px
});

const TitleWaitingTypography = styled(Typography)({
    padding: 8, // 1 * 8px
});

interface GameInsightsProps {
    gameState: GameState;
    cardDeckType?: CardDeckType;
    roomUrlId: string;
}

export default function GameInsights(props: GameInsightsProps): ReactElement {
    const alert = useAlert();
    const { authToken } = useContext(UserContext);
    const [, setIsLoading] = useState(false);
    const [averagePoints, setAveragePoints] = useState("");
    const [averageCard, setAverageCard] = useState("");
    const [alignmentScore, setAlignmentScore] = useState("");
    const [timerText, setTimerText] = useState("This round started a few seconds ago.");
    let startMoment: Moment;
    const timerId = useRef<NodeJS.Timeout>();
    const alignmentTooltips = new Map([
        ["Unknown", "Sorry, no advice"],
        ["Very low", "You are very far from each other, please discuss and re-estimate."],
        ["Low", "You are far from each other, please discuss and re-estimate."],
        ["Medium", "You are relatively far from each other, maybe discuss and re-estimate?"],
        ["High", "You are highly aligned, consider agreeing on the average card."],
        ["Very high", "You are very highly aligned, go with the average card."],
        ["Perfect", "You are perfectly aligned, congrats 🎉"],
    ]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);

                const httpClient = new HttpClient(authToken);
                const response = (await httpClient.get(`/rooms/${props.roomUrlId}/insights`)) as GameInsightsResponse;

                if (response.averagePoints != null) {
                    setAveragePoints(response.averagePoints.toString());
                }

                setAverageCard(response.averageCard);
                setAlignmentScore(response.alignmentScoreText);
                setIsLoading(false);
            } catch {
                setIsLoading(false);
                alert.showMessage(
                    "Sorry, but we could not fetch moderator insights 😔. Please contact support@storypoint.poker if this keeps happening.",
                    "error"
                );
            }
        };

        if (props.gameState == GameState.Result) {
            fetchData();
        }

        if (props.gameState == GameState.Playing) {
            // Clear insights when starting a new round to prevent showing old data
            setAverageCard("");
            setAveragePoints("");
            setAlignmentScore("");
            
            if (timerId.current) {
                clearInterval(timerId.current);
            }
            //TODO pri2: persist startTime as a part of the game data and use that instead to make it more robust. This would not survive a hard refresh
            startMoment = moment();
            timerId.current = setInterval(() => {
                setTimerText("This round started " + startMoment.fromNow() + ".");
            }, 1000);
        }
    }, [props.gameState]);

    function renderByGameState(state: GameState) {
        switch (state) {
            case GameState.Result:
                return (
                    <>
                        {props.cardDeckType != CardDeckType.TShirt && (
                            <>
                                <ItemTitleGrid size={8}>
                                    <TitleTypography variant="body1">
                                        Alignment Score
                                    </TitleTypography>
                                </ItemTitleGrid>
                                <ItemValueGrid size={4}>
                                    <Tooltip title={alignmentTooltips.get(alignmentScore)}>
                                        <Box sx={{ cursor: "pointer" }}>
                                            <AlignmentBadgeComponent
                                                label={alignmentScore}
                                            />
                                        </Box>
                                    </Tooltip>
                                </ItemValueGrid>
                                <ItemTitleGrid size={8}>
                                    <TitleTypography variant="body1">
                                        Average Points
                                    </TitleTypography>
                                </ItemTitleGrid>
                                <ItemValueGrid size={4}>
                                    <Typography variant="body1">{averagePoints}</Typography>
                                </ItemValueGrid>
                            </>
                        )}
                        <ItemTitleGrid size={8}>
                            <TitleTypography variant="body1">
                                Average Card
                            </TitleTypography>
                        </ItemTitleGrid>
                        <ItemValueGrid size={4}>
                            <Typography variant="body1">{averageCard}</Typography>
                        </ItemValueGrid>
                    </>
                );
            case GameState.Playing:
                return (
                    <>
                        <ItemTitleGrid size={12}>
                            <TitleTypography variant="body1">
                                {timerText}
                            </TitleTypography>
                        </ItemTitleGrid>
                    </>
                );

            case GameState.WaitingForPLayers:
                return (
                    <>
                        <ItemTitleWaitingGrid size={12}>
                            <TitleWaitingTypography variant="body1" align="center">
                                We will try to bring you valuable insights to help you make good decisions. Please contact us at support@storypoint.poker if you
                                miss anything. Have a nice session 👋
                            </TitleWaitingTypography>
                        </ItemTitleWaitingGrid>
                    </>
                );
            case GameState.Suspended:
                return (
                    <>
                        <ItemTitleWaitingGrid size={12}>
                            <TitleWaitingTypography variant="body1" align="center">
                                Sorry, but moderator insights is only available for registered users with an active plan 😞
                            </TitleWaitingTypography>
                        </ItemTitleWaitingGrid>
                    </>
                );
        }
    }

    return <WindowGridContainer title="Moderator Insights">{renderByGameState(props.gameState)}</WindowGridContainer>;
}
